import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { SistemaPuntuacionServiceFirestore } from '../../services/SistemaPuntuacionServiceFirestore';
import { SistemaPuntuacion } from '../../models/types';
import { FaPlus, FaEdit, FaTrash } from '../../components/Icons';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid ${theme.colors.primary.main};
  padding-bottom: 15px;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 2.2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  
  svg {
    color: ${theme.colors.primary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: white;
  text-align: left;
  padding: 15px;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  
  &:first-child {
    border-top-left-radius: 8px;
  }
  
  &:last-child {
    border-top-right-radius: 8px;
  }
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
  vertical-align: middle;
  
  tr:last-child & {
    border-bottom: none;
  }
  
  tr:hover & {
    background-color: ${theme.colors.background.default};
  }
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 10px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: ${theme.colors.text.secondary};
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.primary.main};
    opacity: 0.7;
  }
  
  h3 {
    margin: 10px 0;
    color: ${theme.colors.text.primary};
  }
  
  p {
    margin: 0;
  }
`;

// Componente de diálogo de confirmación
const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const DialogTitle = styled.h3`
  margin-top: 0;
  color: ${theme.colors.text.primary};
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const DialogText = styled.p`
  margin-bottom: 20px;
  color: ${theme.colors.text.secondary};
`;

const DialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CancelButton = styled.button`
  background-color: #f1f1f1;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: #e1e1e1;
  }
`;

const DeleteButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 15px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: #c0392b;
  }
`;

const PuntuacionesList: React.FC = () => {
  const { data, loadData } = useContext(AppContext);
  const [sistemaAEliminar, setSistemaAEliminar] = useState<SistemaPuntuacion | null>(null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  
  const handleEliminarClick = (sistema: SistemaPuntuacion) => {
    setSistemaAEliminar(sistema);
    setMostrarConfirmacion(true);
  };
  
  const confirmarEliminacion = async () => {
    if (sistemaAEliminar) {
      try {
        await SistemaPuntuacionServiceFirestore.delete(sistemaAEliminar.id);
        await loadData(); // Recargar datos después de eliminar
        setMostrarConfirmacion(false);
        setSistemaAEliminar(null);
      } catch (error) {
        console.error("Error al eliminar sistema de puntuación:", error);
        alert("Error al eliminar el sistema de puntuación. Inténtalo de nuevo.");
      }
    }
  };
  
  const cancelarEliminacion = () => {
    setMostrarConfirmacion(false);
    setSistemaAEliminar(null);
  };
  
  return (
    <Container>
      <Header>
        <Title><FaPlus /> Gestión de Puntuaciones</Title>
        
        <Link to="/puntuaciones/new">
          <Button>
            <FaPlus /> Nuevo Sistema de Puntuación
          </Button>
        </Link>
      </Header>
      
      {data.sistemasPuntuacion.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Puntos 1er Lugar</Th>
              <Th>Vuelta Rápida</Th>
              <Th>Pole Position</Th>
              <Th>Mejor Consistencia</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {data.sistemasPuntuacion.map(sistema => (
              <tr key={sistema.id}>
                <Td>{sistema.nombre}</Td>
                <Td>{sistema.puntosPorPosicion[0]}</Td>
                <Td>+{sistema.puntosAdicionales.vueltaRapida}</Td>
                <Td>+{sistema.puntosAdicionales.polePosition}</Td>
                <Td>+{sistema.puntosAdicionales.mejorConsistencia}</Td>
                <Td>
                  <Link to={`/puntuaciones/${sistema.id}`}>
                    <ActionButton>
                      <FaEdit />
                    </ActionButton>
                  </Link>
                  <ActionButton onClick={() => handleEliminarClick(sistema)}>
                    <FaTrash />
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyMessage>
          No hay sistemas de puntuación registrados. Crea uno nuevo para comenzar.
        </EmptyMessage>
      )}
      
      {/* Diálogo de confirmación para eliminar */}
      {mostrarConfirmacion && sistemaAEliminar && (
        <ConfirmDialog>
          <DialogContent>
            <DialogTitle>Confirmar eliminación</DialogTitle>
            <DialogText>
              ¿Estás seguro de que deseas eliminar el sistema de puntuación <strong>{sistemaAEliminar.nombre}</strong>?
              <br />
              Esta acción no se puede deshacer.
            </DialogText>
            <DialogButtons>
              <CancelButton onClick={cancelarEliminacion}>Cancelar</CancelButton>
              <DeleteButton onClick={confirmarEliminacion}>Eliminar</DeleteButton>
            </DialogButtons>
          </DialogContent>
        </ConfirmDialog>
      )}
    </Container>
  );
};

export default PuntuacionesList;
